export const gameCategories = [
    { name: "Memory", path: "/games/memory", color: "bg-[#FFF0C9]" },
    { name: "Counting", path: "/games/counting", color: "bg-[#DDF5ED]" },
    { name: "Addition", path: "/games/adding", color: "bg-[#E7E0FA]" },
    { name: "Subtraction", path: "/games/subtracting", color: "bg-[#FFE2DE]" },
    { name: "Multiplication", path: "/games/multiplication", color: "bg-[#DDEFFD]" },
    { name: "Division", path: "/games/division", color: "bg-[#FBE1EE]" },
    { name: "Shapes", path: "/games/shapes", color: "bg-[#E2F0D5]" },
    { name: "Fractions", path: "/games/fraction", color: "bg-[#FFF0C9]" },
];

export const parentGuides = [
    {
        slug: "ages-4-6",
        label: "Ages 4–6",
        seoTitle: "Math Games for Ages 4–6 | MathMinds",
        h1: "Math Games for Kids Ages 4–6",
        path: "/math-games/ages-4-6/",
        description: "Explore math games for kids ages 4–6 that practice counting, number recognition, shapes, memory, and early addition through play.",
        intro: "At this age, short and playful sessions can help children connect numbers with quantities, recognize patterns, and become comfortable trying again.",
        skills: ["Counting and number recognition", "Comparing simple quantities", "Shape and color recognition", "Visual memory"],
        games: ["counting", "shapes", "memory"],
        guidance: [
            "Counting objects helps children understand that the last number they say tells how many objects are in the group. Number-recognition activities then connect that quantity to a written numeral.",
            "Shape and memory games develop observation, matching, and spatial language. Simple addition can begin with visible groups so children understand combining before they are expected to recall facts.",
            "Choose a level where your child answers most questions independently but still pauses to think. If guessing or frustration increases, return to smaller numbers or a more visual activity.",
        ],
        activityNote: "Start with Counting, Shapes, and Memory. When your child confidently counts small groups, introduce the earliest Addition activities.",
    },
    {
        slug: "ages-7-9",
        label: "Ages 7–9",
        seoTitle: "Math Games for Ages 7–9 | MathMinds",
        h1: "Math Games for Kids Ages 7–9",
        path: "/math-games/ages-7-9/",
        description: "Find math games for kids ages 7–9 covering addition, subtraction, multiplication, comparison, and practical mental-math strategies.",
        intro: "Children ages 7–9 are often ready to strengthen number facts while learning to choose efficient ways to solve a problem.",
        skills: ["Addition and subtraction fluency", "Early multiplication", "Mental calculation", "Logical comparison"],
        games: ["adding", "subtracting", "multiplication", "compare"],
        guidance: [
            "Children in this age range can strengthen addition and subtraction facts while learning place-value strategies such as making ten, breaking apart numbers, and checking with the inverse operation.",
            "Multiplication should grow from equal groups and repeated addition into fact recall. Comparison games help children reason about expressions instead of relying only on speed.",
            "Select a level that produces steady success with a few thoughtful challenges. Ask your child to explain one strategy after a round, especially when an answer was difficult.",
        ],
        activityNote: "Use Adding and Subtracting for fluency, Multiplication for equal groups and facts, and Compare for reasoning about values and expressions.",
    },
    {
        slug: "ages-10-12",
        label: "Ages 10–12",
        seoTitle: "Math Games for Ages 10–12 | MathMinds",
        h1: "Math Games for Kids Ages 10–12",
        path: "/math-games/ages-10-12/",
        description: "Explore math games for ages 10–12 that reinforce multiplication, division, fractions, decimals, mental math, and multi-step reasoning.",
        intro: "Older children can use game-based practice to reinforce multi-step skills and build speed without losing conceptual understanding.",
        skills: ["Multiplication and division", "Fractions and decimals", "Mental math strategies", "Multi-step problem solving"],
        games: ["multiplication", "division", "fraction", "decimal"],
        guidance: [
            "At ages 10–12, practice should connect multiplication and division, then apply those relationships to fractions, decimals, ratios, and multi-step calculations.",
            "Fraction and decimal activities are most useful when children estimate first, calculate second, and check whether the result is reasonable.",
            "Increase difficulty through the size and complexity of numbers rather than time pressure alone. If errors repeat, revisit the underlying representation or operation before increasing speed.",
        ],
        activityNote: "Combine Multiplication and Division practice with Fraction and Decimal activities. Alternate fluency rounds with untimed explanation and estimation.",
    },
    {
        slug: "multiplication",
        label: "Multiplication",
        seoTitle: "Multiplication Games for Kids | MathMinds",
        h1: "Multiplication Games for Kids",
        path: "/math-games/multiplication/",
        description: "Help children understand equal groups, notice times-table patterns, and improve multiplication fact recall with MathMinds games.",
        intro: "Multiplication practice works best when children understand equal groups first, then build accurate recall through regular, manageable sessions.",
        skills: ["Equal groups", "Times-table recall", "Patterns in multiplication", "Mental multiplication"],
        games: ["multiplication"],
        guidance: [
            "Begin with arrays, equal groups, and repeated addition so multiplication facts have meaning. Then practice related facts together, such as 3 × 4 and 4 × 3.",
            "Short, repeated sessions usually support recall better than a single long drill. Accuracy should become consistent before a child is asked to work faster.",
            "When a fact is difficult, use a known fact to derive it—for example, find 6 × 7 by using 5 × 7 and adding one more group of seven.",
        ],
        activityNote: "The Multiplication collection includes fact practice, expressions, and faster mental-math formats at different levels.",
    },
    {
        slug: "fractions",
        label: "Fractions",
        seoTitle: "Fraction Games for Kids | MathMinds",
        h1: "Fraction Games for Kids",
        path: "/math-games/fractions/",
        description: "Build fraction understanding through games for comparing, converting, adding, subtracting, multiplying, and dividing fractions.",
        intro: "Fraction games can connect pictures, parts of a whole, and number notation before children move into fraction operations.",
        skills: ["Parts of a whole", "Comparing fractions", "Improper and mixed fractions", "Fraction operations"],
        games: ["fraction"],
        guidance: [
            "Start by connecting fraction symbols to equal parts of shapes and number lines. Children should recognize the denominator as the number of equal parts and the numerator as the parts being considered.",
            "Comparison practice should use benchmarks such as zero, one-half, and one before moving to procedures with common denominators.",
            "Introduce improper fractions, mixed numbers, and operations only after the child can explain what a fraction represents. Visual models remain useful when rules become confusing.",
        ],
        activityNote: "MathMinds Fraction activities include comparison, mixed and improper fractions, and the four fraction operations.",
    },
    {
        slug: "offline",
        label: "Offline Math Games",
        seoTitle: "Offline Math Games for Kids | MathMinds",
        h1: "Offline Math Games for Kids",
        path: "/math-games/offline/",
        description: "Plan focused math practice for travel and low-connectivity situations with offline-friendly MathMinds activities and parent tips.",
        intro: "Offline-friendly practice can make learning easier during travel, quiet time, or in homes where a reliable connection is not always available.",
        skills: ["Independent practice", "Travel-friendly learning", "Focused screen time", "Consistent routines"],
        games: ["counting", "adding", "memory"],
        guidance: [
            "Offline practice is useful for travel, waiting rooms, and predictable daily routines because children can continue without browsing or switching between websites.",
            "Choose and test the activities before leaving a reliable connection. Availability can differ between the MathMinds web and installed app versions, and account or subscription actions may still require internet access.",
            "Pair a short game session with an off-screen activity: count objects, estimate a total, find shapes, or explain a strategy aloud.",
        ],
        activityNote: "Open the relevant MathMinds app and test your chosen games while connected before depending on them offline. The web version requires a connection to load.",
    },
    {
        slug: "ad-free",
        label: "Ad-Free Math Games",
        seoTitle: "Ad-Free Math Games for Kids | MathMinds",
        h1: "Ad-Free Math Games for Kids",
        path: "/math-games/ad-free/",
        description: "Learn how an ad-free math-game environment can reduce distractions and support focused, child-friendly practice with MathMinds.",
        intro: "A learning environment without third-party advertising helps children stay focused and gives parents more confidence about what appears on screen.",
        skills: ["Focused practice", "Child-friendly navigation", "Fewer interruptions", "Parent confidence"],
        games: ["memory", "counting", "multiplication", "fraction"],
        guidance: [
            "Third-party advertisements can interrupt a learning sequence and encourage children to leave the activity they are using. An ad-free environment keeps navigation more predictable.",
            "Parents should still choose an appropriate level, set a reasonable session length, and talk with children about what they practiced.",
            "A useful learning game gives clear feedback without turning every interaction into a purchase prompt. Review subscription details separately from the child’s practice session.",
        ],
        activityNote: "MathMinds activities do not display third-party advertising. Some games or features may depend on the selected access or subscription level.",
    },
];

export const guideBySlug = Object.fromEntries(parentGuides.map((guide) => [guide.slug, guide]));
